import React, { useState, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { motion } from "framer-motion";
import { colors } from "../constants/colors";

const Modal = ({
  isOpen,
  onClose,
  title,
  details = {},
  images,
  backgroundImage,
}) => {
  const allImages = [backgroundImage, ...images];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imgRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1,
    );
  };

  const toggleFullscreen = () => {
    if (imgRef.current) {
      imgRef.current.requestFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: isMobile ? "100vh" : "100vh",
          width: isMobile ? "100%" : undefined,
          maxWidth: isMobile ? "100%" : undefined,
          margin: 0,
          padding: 0,
          borderRadius: isMobile ? 0 : 24,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 50%, #49b152 100%)`,
          overflow: "hidden",
          boxShadow: "0 28px 60px rgba(2, 6, 23, 0.34)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          position: "relative",
        }}
      >
        {/* Animated Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(255,255,255,0.05) 0%, transparent 50%)
            `,
            zIndex: 1,
          }}
        />

        {/* Description Section */}
        <Box
          sx={{
            width: isMobile ? "100%" : "50%",
            height: isMobile ? "52%" : "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Glass Morphism Container */}
          <Box
            sx={{
              position: "absolute",
              top: "20px",
              left: "20px",
              right: "20px",
              bottom: "20px",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: "20px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                zIndex: 10,
                padding: isMobile ? 2 : 3,
                borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconButton
                  edge="end"
                  onClick={onClose}
                  aria-label="close"
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: 16,
                    color: "#ffffff",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1) rotate(90deg)",
                      background: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </motion.div>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  fontFamily: "Inter, sans-serif",
                  fontSize: isMobile ? "2rem" : undefined,
                  lineHeight: isMobile ? 1.08 : undefined,
                  letterSpacing: isMobile ? "-0.04em" : undefined,
                  maxWidth: isMobile ? "74%" : "100%",
                  display: isMobile ? "-webkit-box" : "block",
                  WebkitLineClamp: isMobile ? 2 : "unset",
                  WebkitBoxOrient: isMobile ? "vertical" : "unset",
                  overflow: "hidden",
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* Content */}
            <Box
              sx={{
                overflowY: "auto",
                flexGrow: 1,
                padding: isMobile ? 2 : 3,
                color: "#ffffff",
              }}
            >
              {/* Project Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    color: "#ffffff",
                    fontWeight: 600,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Project Details
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    color: "rgba(255, 255, 255, 0.9)",
                    lineHeight: 1.6,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {details.description || "No description available."}
                </Typography>
              </motion.div>

              {/* My Role */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    color: "#ffffff",
                    fontWeight: 600,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  My Role
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    color: "rgba(255, 255, 255, 0.9)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {details.role || "No role specified."}
                </Typography>
              </motion.div>

              {/* Technologies */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    color: "#ffffff",
                    fontWeight: 600,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Technologies Used
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {Array.isArray(details.technologies) &&
                  details.technologies.length > 0 ? (
                    details.technologies.map((tech, index) => (
                      <motion.span
                        key={index}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: index * 0.1 + 0.4 }}
                        whileHover={{
                          scale: 1.1,
                          background: "rgba(255, 255, 255, 0.3)",
                        }}
                        style={{
                          background: "rgba(255, 255, 255, 0.15)",
                          color: "rgba(255, 255, 255, 0.95)",
                          padding: "6px 12px",
                          borderRadius: "15px",
                          fontSize: "12px",
                          fontWeight: "600",
                          fontFamily: "Inter, sans-serif",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {tech}
                      </motion.span>
                    ))
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      No technologies specified.
                    </Typography>
                  )}
                </Box>
              </motion.div>

              {/* Modules */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    color: "#ffffff",
                    fontWeight: 600,
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Modules
                </Typography>
                {details.modules && details.modules.length > 0 ? (
                  details.modules.map((module, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.5 }}
                      sx={{
                        mt: 2,
                        mb: 2,
                        background: "rgba(255, 255, 255, 0.1)",
                        padding: 2,
                        borderRadius: "12px",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#ffffff",
                          fontWeight: 600,
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {module.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          mb: 1,
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {module.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          mt: 1,
                          color: "#ffffff",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Features:
                      </Typography>
                      <ul>
                        {module.features.map((feature, featureIndex) => (
                          <li key={featureIndex}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              {feature}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    No modules available.
                  </Typography>
                )}
              </motion.div>
            </Box>
          </Box>
        </Box>

        {/* Image Gallery Section */}
        <Box
          sx={{
            width: isMobile ? "100%" : "50%",
            height: isMobile ? "48%" : "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            zIndex: 2,
          }}
        >
          {/* Glass Morphism Image Container */}
          <Box
            sx={{
              position: "absolute",
              top: "20px",
              left: "20px",
              right: "20px",
              bottom: "20px",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: "20px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <motion.img
              ref={imgRef}
              src={allImages[currentImageIndex]}
              alt={`Gallery image ${currentImageIndex + 1}`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "12px",
              }}
            />

            {/* Image Navigation Buttons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {/* Fullscreen Button */}
              <IconButton
                onClick={toggleFullscreen}
                aria-label="toggle fullscreen"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "#ffffff",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  zIndex: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    background: "rgba(255, 255, 255, 0.3)",
                  },
                }}
              >
                <FullscreenIcon />
              </IconButton>

              {/* Previous Button */}
              <IconButton
                onClick={prevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#ffffff",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.3)",
                    transform: "translateY(-50%) scale(1.15)",
                  },
                  zIndex: 2,
                  borderRadius: "50%",
                }}
                aria-label="Previous Image"
              >
                <ArrowBackIcon
                  fontSize="large"
                  sx={{
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateX(-4px)",
                    },
                  }}
                />
              </IconButton>

              {/* Next Button */}
              <IconButton
                onClick={nextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#ffffff",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.3)",
                    transform: "translateY(-50%) scale(1.15)",
                  },
                  zIndex: 2,
                  borderRadius: "50%",
                }}
                aria-label="Next Image"
              >
                <ArrowForwardIcon
                  fontSize="large"
                  sx={{
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateX(4px)",
                    },
                  }}
                />
              </IconButton>
            </motion.div>

            {/* Image Counter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                padding: "8px 16px",
                borderRadius: "20px",
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: "600",
                fontFamily: "Inter, sans-serif",
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              {currentImageIndex + 1} / {allImages.length}
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default Modal;
